/**
 * Component class name composable
 *
 * Provides a unified API for generating BEM class names with modifiers
 * and responsive classes, eliminating the need to repeat component names
 * and manually spread resolveResponsiveClasses results.
 *
 * @example
 * // In component setup:
 * const { root, element } = useComponentClasses("ui-button");
 *
 * const rootClass = computed(() =>
 *   root({
 *     modifiers: { primary: true, disabled: false },
 *     responsive: { size: props.size }
 *   })
 * );
 *
 * // In template:
 * <button :class="rootClass">
 *   <span :class="element('icon')">...</span>
 * </button>
 */

import type { ResponsiveProp } from "../types";

import { bem } from "../utils/bem";
import { resolveResponsiveClasses } from "../utils/resolveResponsiveClasses";

type Modifiers = Record<string, boolean | undefined>;

interface ClassOptions<R extends Record<string, ResponsiveProp<string> | undefined>> {
  modifiers?: Modifiers;
  responsive?: R;
}

export function useComponentClasses(componentName: string) {
  return {
    /**
     * Generates class names for the component root element.
     *
     * Combines BEM block classes with modifiers and responsive classes.
     * Type parameter R is inferred from the responsive object, preserving
     * type safety for prop values.
     *
     * @example
     * root({
     *   modifiers: { primary: true, disabled: false },
     *   responsive: { size: props.size, width: props.width }
     * })
     * // → ["ui-button ui-button--primary", "ui-button--md", "lg:ui-button--lg"]
     */
    root: <R extends Record<string, ResponsiveProp<string> | undefined>>(
      options?: ClassOptions<R>
    ): string[] => {
      const classes: string[] = [bem(componentName, { modifiers: options?.modifiers })];

      for (const propValue of Object.values(options?.responsive ?? {})) {
        if (propValue !== undefined) {
          classes.push(...resolveResponsiveClasses({ baseClass: componentName, propValue }));
        }
      }

      return classes;
    },

    /**
     * Generates class names for child elements using BEM element syntax.
     *
     * Supports modifiers and responsive classes, matching root()'s API.
     *
     * @example
     * element('icon')
     * // → ["ui-button__icon"]
     *
     * element('icon', { modifiers: { large: true } })
     * // → ["ui-button__icon ui-button__icon--large"]
     *
     * element('dropdown', { responsive: { size: 'md' } })
     * // → ["ui-button__dropdown", "ui-button__dropdown--md"]
     */
    element: <R extends Record<string, ResponsiveProp<string> | undefined>>(
      name: string,
      options?: ClassOptions<R>
    ): string[] => {
      const elementName = `${componentName}__${name}`;
      const classes: string[] = [
        bem(componentName, { element: name, modifiers: options?.modifiers }),
      ];

      for (const propValue of Object.values(options?.responsive ?? {})) {
        if (propValue !== undefined) {
          classes.push(...resolveResponsiveClasses({ baseClass: elementName, propValue }));
        }
      }

      return classes;
    },
  };
}
